import { useState } from "react";
import { PageTransition } from "@/components/layout/PageTransition";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useCms } from "@/hooks/useCms";
import { useQuery } from "@tanstack/react-query";
import { db } from "@/lib/firebase";
import { collection, doc, getDoc, addDoc } from "firebase/firestore";
import { incrementCounter } from "@/lib/stats";
import {
  Handshake, Building2, Tv2, Package, GraduationCap, CheckCircle,
  Mail, Phone, Globe, MessageSquare, ChevronDown, Loader2, Lock,
} from "lucide-react";

const inputClass = "w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm";
const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
const selectClass = `${inputClass} appearance-none cursor-pointer`;

const PARTNERSHIP_TYPES = [
  "Title Sponsorship",
  "Kit & Equipment Partnership",
  "Media & Broadcasting",
  "Academy Programme Partner",
  "Technology & Innovation",
  "Community & Grassroots",
  "Financial Services",
  "Other / Custom Partnership",
];

const BENEFITS = [
  { icon: Globe, title: "Global Exposure", desc: "Reach audiences across Africa, Europe, and North America through our athletes, events, and media channels." },
  { icon: GraduationCap, title: "Academy Branding", desc: "Feature your brand prominently in our football academies and talent development camps." },
  { icon: Tv2, title: "Media Visibility", desc: "Co-branded content, match-day coverage, and digital campaigns reaching tens of thousands of fans." },
  { icon: Handshake, title: "CSR Impact", desc: "Align with a mission that develops youth, promotes integrity, and transforms lives through sport." },
];

const PARTNER_TIERS = [
  {
    icon: Building2,
    title: "Title Sponsor",
    color: "from-yellow-500 to-amber-600",
    perks: ["Naming rights on key events", "Premium logo placement on kits", "Exclusive VIP access", "Dedicated PR campaign"],
  },
  {
    icon: Package,
    title: "Kit & Equipment",
    color: "from-blue-600 to-indigo-700",
    perks: ["Logo on training kits", "Equipment branding rights", "Social media mentions", "Annual partner certificate"],
  },
  {
    icon: Tv2,
    title: "Media Partner",
    color: "from-purple-600 to-pink-700",
    perks: ["Co-branded video content", "Broadcast sponsorship slots", "Event coverage rights", "Shared digital audiences"],
  },
  {
    icon: GraduationCap,
    title: "Academy Partner",
    color: "from-emerald-600 to-teal-700",
    perks: ["Youth programme support", "Brand in training facilities", "Grassroots reach", "Community impact reports"],
  },
];

type FormData = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  partnershipType: string;
  message: string;
  agreeTerms: boolean;
};

const emptyForm: FormData = {
  companyName: "", contactName: "", email: "", phone: "",
  website: "", partnershipType: "", message: "", agreeTerms: false,
};

export default function Partner() {
  const { getText } = useCms();
  const heroTitle = getText("partner_hero", "PARTNER WITH US");
  const heroSub = getText("partner_hero_sub", "Join Eliada Sports Agency and connect your brand with the world's most exciting sports talent pipeline. Together, we develop champions — and winning partnerships.");
  const benefitsTitle = getText("partner_benefits_title", "WHY PARTNER WITH ELIADA?");
  const typesTitle = getText("partner_types_title", "PARTNERSHIP OPPORTUNITIES");
  const ctaText = getText("partner_cta", "Ready to start a partnership conversation? Fill in the form and our partnerships team will be in touch within 48 hours.");

  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/settings/site"],
    queryFn: async () => {
      try {
        const snap = await getDoc(doc(db, "configs", "generalSettings"));
        return snap.exists() ? (snap.data() as Record<string, string>) : {};
      } catch {
        return {};
      }
    },
  });
  const partnerFormEnabled = siteSettings?.partner_form_enabled !== "false";

  const [form, setForm] = useState<FormData>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const update = (field: keyof FormData, value: string | boolean) =>
    setForm(p => ({ ...p, [field]: value }));

  const validate = () => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.companyName.trim()) e.companyName = "Company name is required";
    if (!form.contactName.trim()) e.contactName = "Contact name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email is required";
    if (!form.partnershipType) e.partnershipType = "Please select a partnership type";
    if (!form.message.trim() || form.message.trim().length < 20) e.message = "Please provide a message (at least 20 characters)";
    if (!form.agreeTerms) e.agreeTerms = "You must agree to be contacted";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "partner_inquiries"), {
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        partnershipType: form.partnershipType,
        message: form.message.trim(),
        status: "pending",
        notes: "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      try {
        await incrementCounter("partner_inquiries");
      } catch (e) {}
      setSubmitted(true);
    } catch {
      setErrors({ message: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <Helmet>
        <title>Partner With Us | Eliada Sports Agency</title>
        <meta name="description" content="Explore partnership opportunities with Eliada Sports Agency. Connect your brand with elite athlete development across Africa, Europe, and beyond." />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden bg-background">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&auto=format&fit=crop"
            alt="Partnership"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-5 py-2 mb-6">
              <Handshake size={15} className="text-primary" />
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Partnership Opportunities</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-black text-white tracking-tight mb-6">
              {heroTitle.split(" ").map((w, i) =>
                i === 0 ? <span key={i}>{w} </span> : <span key={i} className="text-primary">{w} </span>
              )}
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">{heroSub}</p>
          </motion.div>
        </div>
      </section>

      {/* ── Why Partner ── */}
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-black text-white mb-4">{benefitsTitle}</h2>
            <div className="h-1 w-20 bg-primary mx-auto rounded-full" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-primary/30 hover:bg-white/8 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <b.icon size={22} className="text-primary" />
                </div>
                <h3 className="font-display font-bold text-white text-lg mb-2">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partnership Tiers ── */}
      <section className="py-20 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-black text-white mb-4">{typesTitle}</h2>
            <div className="h-1 w-20 bg-primary mx-auto rounded-full" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PARTNER_TIERS.map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 group">
                <div className={`h-1.5 w-full bg-gradient-to-r ${tier.color}`} />
                <div className="p-6">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <tier.icon size={20} className="text-white" />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-4">{tier.title}</h3>
                  <ul className="space-y-2">
                    {tier.perks.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                        <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form ── */}
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-display text-4xl font-black text-white mb-4">GET IN TOUCH</h2>
            <p className="text-gray-400 leading-relaxed max-w-xl mx-auto">{ctaText}</p>
          </motion.div>

          {!partnerFormEnabled ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-5">
                <Lock size={26} className="text-gray-400" />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-3">Partnership Enquiries Paused</h3>
              <p className="text-gray-400 mb-6">We are not accepting new partnership enquiries at this time. Please check back soon or reach out to us directly.</p>
              <a href="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors">
                <Mail size={16} /> Contact Us Directly
              </a>
            </motion.div>
          ) : submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-12 text-center shadow-xl">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <h3 className="font-display text-3xl font-black text-gray-900 mb-3">Thank You!</h3>
              <p className="text-gray-600 text-lg mb-2">Your partnership inquiry has been received.</p>
              <p className="text-gray-500">Our partnerships team will review your submission and get back to you within 48 hours.</p>
            </motion.div>
          ) : (
            <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-10 space-y-6">

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Company / Organisation Name <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={form.companyName} onChange={e => update("companyName", e.target.value)}
                      placeholder="Acme Corp" className={`${inputClass} pl-9`} />
                  </div>
                  {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                </div>
                <div>
                  <label className={labelClass}>Contact Person Name <span className="text-red-500">*</span></label>
                  <input type="text" value={form.contactName} onChange={e => update("contactName", e.target.value)}
                    placeholder="John Smith" className={inputClass} />
                  {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Email Address <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" value={form.email} onChange={e => update("email", e.target.value)}
                      placeholder="john@company.com" className={`${inputClass} pl-9`} />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className={labelClass}>Phone Number</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="tel" value={form.phone} onChange={e => update("phone", e.target.value)}
                      placeholder="+1 234 567 8900" className={`${inputClass} pl-9`} />
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>Company Website</label>
                <div className="relative">
                  <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="url" value={form.website} onChange={e => update("website", e.target.value)}
                    placeholder="https://www.company.com" className={`${inputClass} pl-9`} />
                </div>
              </div>

              <div>
                <label className={labelClass}>Partnership Type <span className="text-red-500">*</span></label>
                <div className="relative">
                  <select value={form.partnershipType} onChange={e => update("partnershipType", e.target.value)} className={selectClass}>
                    <option value="">Select a partnership type…</option>
                    {PARTNERSHIP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                {errors.partnershipType && <p className="text-red-500 text-xs mt-1">{errors.partnershipType}</p>}
              </div>

              <div>
                <label className={labelClass}>Tell Us About Your Partnership Idea <span className="text-red-500">*</span></label>
                <div className="relative">
                  <MessageSquare size={16} className="absolute left-3 top-3 text-gray-400" />
                  <textarea value={form.message} onChange={e => update("message", e.target.value)} rows={5}
                    placeholder="Describe the partnership opportunity, what you are offering, and what you hope to achieve together…"
                    className={`${inputClass} pl-9 resize-none`} />
                </div>
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>

              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.agreeTerms} onChange={e => update("agreeTerms", e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30" />
                  <span className="text-sm text-gray-600">
                    I consent to Eliada Sports Agency contacting me regarding this partnership inquiry and agree to the{" "}
                    <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
                  </span>
                </label>
                {errors.agreeTerms && <p className="text-red-500 text-xs mt-1">{errors.agreeTerms}</p>}
              </div>

              <button type="submit" disabled={submitting}
                className="w-full bg-primary text-primary-foreground font-display font-bold text-base py-4 rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting…</> : <><Handshake size={18} /> Submit Partnership Inquiry</>}
              </button>
            </motion.form>
          )}
        </div>
      </section>
    </PageTransition>
  );
}
